import cx from 'classnames'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { observer } from 'mobx-react-lite'
import { DefaultDisposer } from 'stores/TrackedChanges/DefaultDisposer'
import { MergeDisposer } from 'stores/TrackedChanges/MergeDisposer'
import Styles from './CollabItemInlineWidget.module.scss'
import { offset, flip, shift, useFloating, autoUpdate, inline, hide } from '@floating-ui/react-dom'
import { useLayoutEffect } from 'react'
import { computed } from 'mobx'
import { CollabItemInlineWidgetProps } from 'components/CollabItemInlineWidget/Types'
import { CollabItemActions } from 'components/CollabItemActions/CollabItemActions'
import { CollabItemStageActions } from 'components/CollabItemStageActions/CollabItemStageActions'
import { CollabItemSummary } from 'components/CollabItemInlineWidget/CollabItemSummary'
import { ChevronLeft, ChevronRight } from 'almanac-ui/icons/12'
import { Button } from 'components/Button'

export const CollabItemInlineWidget = observer(({ store, anchor, uuid }: CollabItemInlineWidgetProps) => {
  const {
    x,
    y,
    reference,
    floating,
    strategy,
    middlewareData: { hide: { escaped } = {} },
  } = useFloating({
    strategy: 'fixed',
    placement: 'top',
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
    middleware: [inline(), offset(2), flip(), shift(), hide({ strategy: 'escaped', padding: 40 })],
  })

  useLayoutEffect(() => {
    reference(anchor)
  }, [anchor, reference])

  const style = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
    display: escaped ? 'none' : 'flex',
    zIndex: 1,
  }

  const {
    accept,
    reject,
    select,
    selectNext,
    nextItem,
    selectPrevious,
    previousItem,
    isDispositioning,
    canAcceptItems,
    canRejectItems,
    disposer,
    items,
  } = store

  const collabItem = items.find((collabItem) => collabItem.item.uuid === uuid)

  if (!collabItem) {
    return null
  }

  const isActive = computed(() => store.selectedItem?.item.uuid === uuid).get()

  const handleAccept = async () => {
    const nextUuid = nextItem?.item.uuid || undefined
    try {
      await accept([uuid])
      select(nextUuid)
    } catch (error) {
      if (error instanceof Error) showToastMessage(error.message, 'error')
    }
  }

  const handleReject = () => {
    const nextUuid = nextItem?.item.uuid || undefined
    reject(uuid)
    select(nextUuid)
  }

  return (
    <div
      className={cx(Styles.root, { [Styles.active]: isActive })}
      data-click-outside="ignore"
      ref={floating}
      style={style}
    >
      <div className={Styles.summary}>
        <CollabItemSummary collabItem={collabItem} isActive={isActive} />
      </div>

      {isActive && (
        <>
          <div className={Styles.buttonGroup}>
            <Button frontIcon={ChevronLeft} variant="tertiary" onPress={selectPrevious} isDisabled={!previousItem}>
              Prev
            </Button>
            <Button endIcon={ChevronRight} variant="tertiary" onPress={selectNext} isDisabled={!nextItem}>
              Next
            </Button>
          </div>
          {disposer instanceof DefaultDisposer && (
            <CollabItemActions
              item={{ uuid }}
              onAccept={handleAccept}
              onReject={handleReject}
              isAcceptDisabled={isDispositioning || !canAcceptItems}
              isRejectDisabled={isDispositioning || !canRejectItems}
              canAcceptItems={canAcceptItems}
              canRejectItems={canRejectItems}
            />
          )}
          {disposer instanceof MergeDisposer && (
            <CollabItemStageActions
              item={{ uuid }}
              isStaged={disposer.merge.includes(uuid)}
              onAccept={() => accept([uuid])}
              onReject={() => reject(uuid)}
            />
          )}
        </>
      )}
    </div>
  )
})
